import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {ApplicationInterface, KeyValueInterface, NameValueInterface, experienceLevels} from "../interfaces";
import {Modal, ModalHeader, ModalBody} from "../../../components/Modal";
import CustomButton from "../../../components/CustomButton";
import AlertIcon from "../../../components/Icons/AlertIcon";
import InformationCircleIcon from "../../../components/Icons/InformationCircleIcon";
import RadioBox from "../../../components/Boxes/RadioBox";
import {scrollTop} from "../../../helpers/scrolling";
import {getLanguage} from "../../../lang/helpers";

interface Props {
    application: ApplicationInterface;
    experiencesByArea: Array<KeyValueInterface>
    save: Function
}

const StepFive: React.FC<Props> = (props: Props) => {
    const translation = useTranslation();
    const { t, i18n } = translation;
    const lang = getLanguage(i18n.language)
    const [areas, setAreas] = useState<Array<NameValueInterface>>(props.application.experienceByArea);
    const [additionalAreas, setAdditionalAreas] = useState<string>(props.application.additionalAreas)
    const [error, setError] = useState<string>('')
    const [triedSave, setTriedSave] = useState<boolean>(false)
    const [showModal, setShowModal] = useState<boolean>(false)

    useEffect(() => {
        scrollTop()
    }, [])

    useEffect(() => {
        if (triedSave) {
            checkInput()
        }
    }, [areas])

    const checkInput = () => {
        let _not_none = 0
        let _error = ''
        for (const a of areas) {
            if (a.value > 0) _not_none++
        }
        if (areas.length < props.experiencesByArea.length) {
            _error = "Please select an option for all coaching areas"
        } else if (_not_none < 1) {
            _error = "You need to have experience in at least one coaching area"
        }
        setError(_error)
        return _error
    }

    const save = () => {
        setTriedSave(true)
        if (checkInput().length > 0) {
            return
        }
        const _application: ApplicationInterface = props.application
        _application.experienceByArea = areas
        _application.additionalAreas = additionalAreas
        props.save(_application)
    }

    const selectOption = (key: string, value: number) => {
        let found = false
        const _areas = [...areas]
        for (const a of _areas) {
            if (a.name === key) {
                a.value = value
                found = true
                break
            }
        }
        if (!found) {
            _areas.push({
                name: key,
                value: value
            })
        }
        setAreas(_areas)
    }

    const renderOption = (key: string) => {
        let value = -1
        for (const a of areas) {
            if (a.name === key) {
                value = a.value
            }
        }
        return (
          <>
              {experienceLevels.map(level => (
                <div key={"option_" + key + "_" + level.value} className="mt-2 flex items-center cursor-pointer"
                     onClick={() => selectOption(key, level.value)}>
                    <RadioBox checked={level.value === value}/>
                    <div className="ml-2">{level.name}</div>
                </div>
              ))}
          </>
        )
    }

    const renderAreas = (start: number, end: number) => {
        return (
          <div className="flex flex-wrap">
              {
                  props.experiencesByArea.slice(start, end).map((area: KeyValueInterface, index: number) => (
                    <div key={"area_" + area.key + "_" + (start + index)} className="mt-4" style={{width: 280}}>
                        <div className="font-medium">
                            {area.value}
                        </div>
                        {renderOption(area.key)}
                    </div>
                  ))
              }
          </div>
        )
    }

    return (
      <>
          <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
              <ModalHeader onClose={() => setShowModal(false)}>
                  Coaching experience by area
              </ModalHeader>
              <ModalBody>
                  <p className="font-medium">
                      Extensive:
                  </p>
                  <p className="text-sm">
                      Significant hands-on experience, applied frequently.
                  </p>

                  <p className="mt-4 font-medium">
                      Moderate:
                  </p>
                  <p className="text-sm">
                      Practical experience, applied occasionally.
                  </p>

                  <p className="mt-4 font-medium">
                      Basic:
                  </p>
                  <p className="text-sm">
                      Foundational experience, applied a few times.
                  </p>
              </ModalBody>
          </Modal>

          <div className="mt-6 flex items-center">
              <div className="text-xl font-medium">Coaching experience by area</div>
              <InformationCircleIcon className="ml-2 cursor-pointer" alt="info" onClick={() => setShowModal(true)}/>
          </div>

          <div className="mt-4 text-neutral-400">
              Please indicate your level of experience in each coaching area.
          </div>

          <div className="mt-6 text-lg font-medium">
              Leadership and Team Development
          </div>

          {renderAreas(0, 3)}

          <hr className="mt-4 border-neutral-600"/>

          <div className="mt-6 text-lg font-medium">
              Organisational and Business coaching
          </div>

          {renderAreas(3, 6)}

          <hr className="mt-4 border-neutral-600"/>

          <div className="mt-6 text-lg font-medium">
              Personal development
          </div>

          {renderAreas(6, 12)}

          <hr className="mt-4 border-neutral-600"/>

          <div className="mt-6 text-lg font-medium">
              Career and Professional Growth
          </div>

          {renderAreas(12, 15)}

          <hr className="mt-4 border-neutral-600"/>

          <div className="mt-6">{lang === 'sv' ? 'Andra områden du coachar inom' : 'Additional areas'}</div>
          <input value={additionalAreas}
                 className="z-input mt-2"
                 onChange={(e) => setAdditionalAreas(e.target.value)}/>

          {
            (triedSave && error.length > 0) &&
              <div className="mt-6 p-4 bg-neutral-700 border rounded-md border-cherry-500 flex items-center">
                  <AlertIcon aria-label="Error" className="text-cherry-500!"/>
                  <div className="ml-2 font-medium">{error}</div>
              </div>
          }


          <CustomButton className="mt-6" onClick={() => save()}>
              Next
          </CustomButton>
      </>
    )
}

export default StepFive